import { createFileRoute, redirect } from '@tanstack/react-router'

import { caseKeys, caseQueries } from '@/features/air/case-management/queries'

import { getCurrentTenant, getTenantFeatures } from '@/shared/auth/store'

import type {
	CaseDetailsResponse,
	CaseList,
} from '@/features/air/case-management/types'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'

export const Route = createFileRoute('/_dashboard/air/case-management/$id/')({
	// The bare case route never renders — it is a switchboard that forwards to the
	// correct landing tab. Investigation present → Behavior; otherwise → Case
	// Details.
	//
	// Tenants without `beta_enable_automated_investigation` never see the
	// investigation tabs, so we short-circuit to Case Details — no cache scan, no
	// fetch. Fail closed: a missing/undefined `features` payload gates off too.
	//
	// When the flag is on: all `beforeLoad`s run before any `loader`, so the
	// parent's case-detail fetch is NOT yet resolved here. We therefore decide
	// from the warm cases-list cache first (which carries a per-case
	// `investigation` summary and is hot whenever the user arrives from the table)
	// — instant, no fetch, no "wrong tab then bounce" flash. Deep-links /
	// refreshes with no list in memory fall back to the case-detail response,
	// fetching it only when the cache is cold (the parent loader hits the same
	// key; React Query dedupes it).
	beforeLoad: async ({ params, search, context: { queryClient } }) => {
		const tenant = getCurrentTenant() as string

		const investigationEnabled = Boolean(
			getTenantFeatures()?.beta_enable_automated_investigations,
		)

		let hasInvestigation = investigationEnabled
			? findInvestigationInListCache(queryClient, params.id)
			: false

		if (investigationEnabled && hasInvestigation === undefined) {
			const detail =
				queryClient.getQueryData<CaseDetailsResponse>(
					caseKeys.case_details(tenant, params.id),
				) ??
				(await queryClient.ensureQueryData(
					caseQueries.case_details(tenant, params.id),
				))
			hasInvestigation = detail?.investigation != null
		}

		throw redirect({
			to: hasInvestigation
				? '/air/case-management/$id/behavior'
				: '/air/case-management/$id/case-details',
			params: { id: params.id },
			search,
			replace: true,
		})
	},
})

/**
 * Scan the warm cases-list cache for a case by id and report whether it carries
 * an investigation summary. Returns `undefined` when the case is not in any list
 * page (e.g. deep-link), signalling the caller to fall back to the case detail.
 *
 * @param queryClient - The router's query client.
 * @param id - The case (alert) id to resolve.
 * @returns `true`/`false` when found in a list page, otherwise `undefined`.
 */
function findInvestigationInListCache(
	queryClient: QueryClient,
	id: string,
): boolean | undefined {
	const entries = queryClient.getQueriesData<InfiniteData<CaseList>>({
		queryKey: [...caseKeys.all, 'list'],
	})
	for (const [, data] of entries) {
		const match = data?.pages
			.flatMap((page) => page.cases)
			.find((item) => item.alert_id === id)
		if (match) return match.investigation != null
	}
	return undefined
}
